<script setup lang="ts">
import { PhCaretCircleLeft, PhCaretCircleRight } from '@phosphor-icons/vue'
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
  shallowRef,
  watch,
} from 'vue'
import { storeToRefs } from 'pinia'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useElementWidth } from '@/composables/useElementWidth'
import { useMediaLifecycle } from '@/composables/useMediaLifecycle'
import { useMediaSoundPreference } from '@/composables/useMediaSoundPreference'
import { DISTRIBUTE_ANCHOR_LABEL, DISTRIBUTE_IMAGE_AUTOPLAY_INTERVAL } from '@/constants/distribute'
import { useFeedPreferencesStore } from '@/stores/feedPreferences'
import {
  buildDisplayMediaImageUrl,
  buildLowQualityImagePreviewUrl,
  resolveMediaPreviewWidth,
  resolveMediaRequestWidth,
} from '@/utils/distribute/media-url'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import CoverMetaOverlay from '@/components/distribute-cover-meta-overlay'
import MediaSoundToggle from '@/components/distribute-media-sound-toggle'
import type { Emits, Props } from './type'

defineOptions({
  name: 'DistributeImageCard',
})

type GalleryDirection = 'previous' | 'next'

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const runtimeBudget = getRuntimePerformanceBudget()
const feedPreferencesStore = useFeedPreferencesStore()
const { isDiscoverAutoplayEnabled } = storeToRefs(feedPreferencesStore)

const cardElement = ref<HTMLElement | null>(null)
const audioElement = ref<HTMLAudioElement | null>(null)
const { isVisible } = useElementVisibility(cardElement)
const { width: measuredWidth } = useElementWidth(cardElement)
const { isVisible: shouldLoadMedia } = useElementVisibility(cardElement, {
  initialVisible: false,
  rootMargin: runtimeBudget.mediaPreloadRootMargin,
  threshold: 0,
})
const { isPlaybackMuted, toggleSound } = useMediaSoundPreference()
const canAutoplayPreview = computed(() => isDiscoverAutoplayEnabled.value && props.autoplayActive)
const { play, stop } = useMediaLifecycle(audioElement, {
  autoPlayWhenVisible: canAutoplayPreview,
  muted: isPlaybackMuted,
  visibilityTarget: cardElement,
})
const currentImageIndex = shallowRef(0)
const isHovering = shallowRef(false)
const activeCoverSrc = shallowRef<string | null>(null)
const activeCoverLoaded = shallowRef(false)
const activeCoverFailed = shallowRef(false)
const activeCoverPreviewLoaded = shallowRef(false)
const activeCoverPreviewFailed = shallowRef(false)
const previousCoverSrc = shallowRef<string | null>(null)
const pendingGalleryDirection = shallowRef<GalleryDirection | null>(null)
const loadingGalleryDirection = shallowRef<GalleryDirection | null>(null)
const hasEmittedPreviewReady = shallowRef(false)
let carouselTimer: number | null = null
let coverTransitionTimer: number | null = null
let gallerySwitchLoadingTimer: number | null = null
let gallerySwitchToken = 0

const isMediaVariant = computed(() => props.variant === 'media')
const galleryImages = computed(() =>
  props.model.previewImages.filter((image) => Boolean(image.src)),
)
const galleryImageCount = computed(() => galleryImages.value.length)
const hasGalleryControls = computed(() => galleryImageCount.value > 1)
const selectedGalleryImage = computed(
  () => galleryImages.value[currentImageIndex.value] ?? galleryImages.value[0] ?? null,
)
const coverSrc = computed(() =>
  hasGalleryControls.value || isMediaVariant.value
    ? selectedGalleryImage.value?.src || props.model.coverUrl
    : props.model.coverUrl,
)
const layoutWidth = computed(() =>
  typeof props.mediaWidth === 'number' && props.mediaWidth > 0
    ? props.mediaWidth
    : measuredWidth.value,
)
const requestWidth = computed(() =>
  resolveMediaRequestWidth(layoutWidth.value, {
    dpr: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
  }),
)
const previewWidth = computed(() => resolveMediaPreviewWidth(layoutWidth.value))
const activeCoverDisplaySrc = computed(() =>
  buildDisplayMediaImageUrl(activeCoverSrc.value, {
    height: 0,
    width: requestWidth.value,
  }),
)
const previousCoverDisplaySrc = computed(() =>
  buildDisplayMediaImageUrl(previousCoverSrc.value, {
    height: 0,
    width: requestWidth.value,
  }),
)
const activeCoverPreviewSrc = computed(() =>
  buildLowQualityImagePreviewUrl(activeCoverSrc.value, {
    height: 0,
    width: previewWidth.value,
  }),
)
const shouldRenderCoverPreview = computed(
  () =>
    Boolean(activeCoverPreviewSrc.value) &&
    activeCoverPreviewSrc.value !== activeCoverSrc.value &&
    !activeCoverPreviewFailed.value,
)
const previewLoading = computed(() => (props.priority ? 'eager' : 'lazy'))
const previewFetchPriority = computed(() => (props.priority ? 'high' : 'auto'))
const activeImageLoading = computed(() => (props.priority ? 'eager' : 'lazy'))
const shouldPauseCarouselOnHover = computed(() => props.model.kind === 'media-imgtxt')
const isGallerySwitchPending = computed(() => Boolean(pendingGalleryDirection.value))
const previewAudioUrl = computed(() => props.model.previewAudioUrl)
const shouldRenderAudio = computed(() => Boolean(previewAudioUrl.value))
const shouldAutoAdvanceGallery = computed(() => canAutoplayPreview.value)
const overlayState = computed(() => ({
  showAnchor: props.variant === 'pipe',
  anchorLabel: DISTRIBUTE_ANCHOR_LABEL,
  showBgm: isMediaVariant.value && Boolean(props.model.previewAudioUrl),
}))

watch(
  () => props.model.item.id,
  () => {
    gallerySwitchToken += 1
    clearGallerySwitchLoadingTimer()
    pendingGalleryDirection.value = null
    loadingGalleryDirection.value = null
    hasEmittedPreviewReady.value = false
    currentImageIndex.value = 0
  },
)

function showPreviousImage() {
  void requestGalleryImage('previous')
}

function showNextImage() {
  void requestGalleryImage('next')
}

function resolveGalleryTargetIndex(direction: GalleryDirection): number | null {
  if (galleryImageCount.value <= 1 || isGallerySwitchPending.value) {
    return null
  }

  if (direction === 'previous') {
    return (currentImageIndex.value - 1 + galleryImageCount.value) % galleryImageCount.value
  }

  return (currentImageIndex.value + 1) % galleryImageCount.value
}

function resolveGalleryImageSrc(index: number): string | null {
  return galleryImages.value[index]?.src ?? null
}

function resolveCoverDisplaySrc(src: string | null): string | null {
  return buildDisplayMediaImageUrl(src, {
    height: 0,
    width: requestWidth.value,
  })
}

function resolveCoverPreviewSrc(src: string | null): string | null {
  const previewSrc = buildLowQualityImagePreviewUrl(src, {
    height: 0,
    width: previewWidth.value,
  })

  return previewSrc && previewSrc !== src ? previewSrc : null
}

function preloadImage(src: string | null): Promise<boolean> {
  return new Promise((resolve) => {
    if (!src || typeof window === 'undefined' || typeof window.Image === 'undefined') {
      resolve(false)
      return
    }

    const image = new window.Image()

    image.decoding = 'async'
    image.onload = () => resolve(true)
    image.onerror = () => resolve(false)
    image.src = src
  })
}

function clearGallerySwitchLoadingTimer() {
  if (!gallerySwitchLoadingTimer || typeof window === 'undefined') {
    gallerySwitchLoadingTimer = null
    return
  }

  window.clearTimeout(gallerySwitchLoadingTimer)
  gallerySwitchLoadingTimer = null
}

function startGallerySwitchLoadingTimer(direction: GalleryDirection) {
  clearGallerySwitchLoadingTimer()

  if (typeof window === 'undefined') {
    return
  }

  gallerySwitchLoadingTimer = window.setTimeout(() => {
    loadingGalleryDirection.value = direction
    gallerySwitchLoadingTimer = null
  }, 160)
}

function finishGallerySwitch(token: number) {
  if (token !== gallerySwitchToken) {
    return
  }

  clearGallerySwitchLoadingTimer()
  pendingGalleryDirection.value = null
  loadingGalleryDirection.value = null
}

async function requestGalleryImage(direction: GalleryDirection) {
  const targetIndex = resolveGalleryTargetIndex(direction)

  if (targetIndex === null) {
    return
  }

  const targetSrc = resolveGalleryImageSrc(targetIndex)

  if (!targetSrc) {
    return
  }

  gallerySwitchToken += 1

  const token = gallerySwitchToken
  const targetPreviewSrc = resolveCoverPreviewSrc(targetSrc)
  const targetDisplaySrc = resolveCoverDisplaySrc(targetSrc) ?? targetSrc

  pendingGalleryDirection.value = direction
  loadingGalleryDirection.value = null
  startGallerySwitchLoadingTimer(direction)

  if (targetPreviewSrc) {
    const previewLoaded = await preloadImage(targetPreviewSrc)

    if (token !== gallerySwitchToken) {
      return
    }

    if (previewLoaded) {
      currentImageIndex.value = targetIndex
      commitCoverSrc(targetSrc, { previewLoaded: true })
      finishGallerySwitch(token)
      return
    }
  }

  const fullLoaded = await preloadImage(targetDisplaySrc)

  if (token !== gallerySwitchToken) {
    return
  }

  if (fullLoaded) {
    currentImageIndex.value = targetIndex
    commitCoverSrc(targetSrc, { loaded: true })
  }

  finishGallerySwitch(token)
}

function clearCoverTransitionTimer() {
  if (!coverTransitionTimer) {
    return
  }

  window.clearTimeout(coverTransitionTimer)
  coverTransitionTimer = null
}

function clearPreviousCover() {
  previousCoverSrc.value = null
  clearCoverTransitionTimer()
}

function commitCoverSrc(
  nextSrc: string | null,
  options: { loaded?: boolean; previewLoaded?: boolean } = {},
) {
  if (nextSrc === activeCoverSrc.value) {
    emitPreviewReady()
    return
  }

  clearCoverTransitionTimer()

  const previousSrc = activeCoverSrc.value
  previousCoverSrc.value = previousSrc && previousSrc !== nextSrc ? previousSrc : null
  activeCoverSrc.value = nextSrc
  activeCoverLoaded.value = Boolean(nextSrc && options.loaded)
  activeCoverFailed.value = false
  activeCoverPreviewLoaded.value = Boolean(nextSrc && options.previewLoaded)
  activeCoverPreviewFailed.value = false
  emitPreviewReady()

  if (!previousCoverSrc.value || typeof window === 'undefined') {
    clearPreviousCover()
    return
  }

  coverTransitionTimer = window.setTimeout(clearPreviousCover, 420)
}

function transitionToCover(nextSrc: string | null) {
  commitCoverSrc(nextSrc)
}

function markActiveCoverLoaded() {
  activeCoverLoaded.value = true
  emitPreviewReady()
}

function markActiveCoverFailed() {
  activeCoverFailed.value = true
  emitPreviewReady()
}

function markActiveCoverPreviewLoaded() {
  activeCoverPreviewLoaded.value = true
  emitPreviewReady()
}

function markActiveCoverPreviewFailed() {
  activeCoverPreviewFailed.value = true
}

function stopCarousel() {
  if (!carouselTimer) {
    return
  }

  window.clearInterval(carouselTimer)
  carouselTimer = null
}

function startCarousel() {
  stopCarousel()

  if (
    !isVisible.value ||
    !shouldLoadMedia.value ||
    !shouldAutoAdvanceGallery.value ||
    galleryImageCount.value <= 1 ||
    (shouldPauseCarouselOnHover.value && isHovering.value) ||
    typeof window === 'undefined'
  ) {
    return
  }

  carouselTimer = window.setInterval(showNextImage, DISTRIBUTE_IMAGE_AUTOPLAY_INTERVAL)
}

function handleMouseEnter() {
  isHovering.value = true

  if (shouldPauseCarouselOnHover.value) {
    stopCarousel()
  }

  void play()
}

function handleMouseLeave() {
  isHovering.value = false
  stop()
  startCarousel()
}

function handleToggleSound() {
  toggleSound()
  void play()
}

function emitPreviewReady() {
  if (
    hasEmittedPreviewReady.value ||
    (activeCoverSrc.value &&
      !activeCoverLoaded.value &&
      !activeCoverPreviewLoaded.value &&
      !activeCoverFailed.value)
  ) {
    return
  }

  hasEmittedPreviewReady.value = true
  emit('preview-ready')
}

watch(coverSrc, transitionToCover, { immediate: true })
watch(
  shouldRenderAudio,
  async (renderAudio) => {
    if (!renderAudio || !isHovering.value) {
      return
    }

    await nextTick()
    void play()
  },
  { flush: 'post' },
)
watch(
  [
    isVisible,
    shouldLoadMedia,
    galleryImageCount,
    isHovering,
    shouldPauseCarouselOnHover,
    shouldAutoAdvanceGallery,
  ],
  startCarousel,
  {
    immediate: true,
  },
)

function teardownInteractiveEffects() {
  gallerySwitchToken += 1
  stop()
  stopCarousel()
  clearPreviousCover()
  clearGallerySwitchLoadingTimer()
  pendingGalleryDirection.value = null
  loadingGalleryDirection.value = null
}

onActivated(() => {
  startCarousel()
})

onDeactivated(() => {
  teardownInteractiveEffects()
})

onBeforeUnmount(() => {
  teardownInteractiveEffects()
})
</script>

<template>
  <div
    ref="cardElement"
    class="image-card"
    :class="`is-${variant}`"
    :style="{ '--preview-aspect': model.aspectRatio }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="image-card__cover-stack" aria-hidden="true">
      <img
        v-if="shouldRenderCoverPreview"
        :key="activeCoverPreviewSrc ?? 'cover-preview'"
        class="image-card__cover-image image-card__cover-image--preview"
        :class="{ 'is-loaded': activeCoverPreviewLoaded }"
        :src="activeCoverPreviewSrc ?? undefined"
        alt=""
        decoding="async"
        draggable="false"
        :loading="previewLoading"
        :fetchpriority="previewFetchPriority"
        @load="markActiveCoverPreviewLoaded"
        @error="markActiveCoverPreviewFailed"
      />
      <img
        v-if="previousCoverDisplaySrc"
        class="image-card__cover-image image-card__cover-image--previous"
        :src="previousCoverDisplaySrc"
        alt=""
        decoding="async"
        draggable="false"
      />
      <img
        v-if="activeCoverDisplaySrc"
        :key="activeCoverDisplaySrc"
        class="image-card__cover-image image-card__cover-image--active"
        :class="{ 'is-loaded': activeCoverLoaded, 'is-fading-in': previousCoverSrc }"
        :src="activeCoverDisplaySrc"
        alt=""
        decoding="async"
        draggable="false"
        :loading="activeImageLoading"
        @load="markActiveCoverLoaded"
        @error="markActiveCoverFailed"
      />
    </div>
    <audio
      v-if="shouldRenderAudio"
      ref="audioElement"
      :src="previewAudioUrl ?? undefined"
      preload="metadata"
    />

    <MediaSoundToggle
      v-if="shouldRenderAudio"
      :muted="isPlaybackMuted"
      @toggle="handleToggleSound"
    />

    <div
      v-if="hasGalleryControls"
      class="image-card__controls"
      :class="{ 'is-pending': isGallerySwitchPending }"
    >
      <button
        type="button"
        class="image-card__control"
        :class="{ 'is-loading': loadingGalleryDirection === 'previous' }"
        :aria-busy="loadingGalleryDirection === 'previous'"
        :aria-label="loadingGalleryDirection === 'previous' ? '正在加载上一张图片' : '上一张图片'"
        :disabled="isGallerySwitchPending"
        @click.stop="showPreviousImage"
      >
        <span v-if="loadingGalleryDirection === 'previous'" class="image-card__loading-ring" />
        <PhCaretCircleLeft v-else :size="24" weight="fill" />
      </button>
      <button
        type="button"
        class="image-card__control"
        :class="{ 'is-loading': loadingGalleryDirection === 'next' }"
        :aria-busy="loadingGalleryDirection === 'next'"
        :aria-label="loadingGalleryDirection === 'next' ? '正在加载下一张图片' : '下一张图片'"
        :disabled="isGallerySwitchPending"
        @click.stop="showNextImage"
      >
        <span v-if="loadingGalleryDirection === 'next'" class="image-card__loading-ring" />
        <PhCaretCircleRight v-else :size="24" weight="fill" />
      </button>
    </div>

    <CoverMetaOverlay
      :model="model"
      :show-anchor="overlayState.showAnchor"
      :anchor-label="overlayState.anchorLabel"
      :show-bgm="overlayState.showBgm"
      :require-login="requireLogin"
      @login="$emit('login')"
    />
  </div>
</template>

<style scoped>
.image-card {
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--preview-aspect);
  border-radius: 12px;
  background: var(--app-skeleton-base);
}

.image-card__cover-stack,
.image-card__cover-image {
  position: absolute;
  inset: 0;
}

.image-card__cover-stack {
  z-index: 0;
  overflow: hidden;
  background: var(--app-skeleton-base);
}

.image-card__cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
}

.image-card__cover-image--preview {
  z-index: 1;
  filter: blur(14px);
  opacity: 0;
  transform: scale(1.06);
  transition: opacity 0.24s ease;
}

.image-card__cover-image--preview.is-loaded {
  opacity: 1;
}

.image-card__cover-image--previous {
  z-index: 2;
  animation: image-cover-fade-out 0.42s ease both;
}

.image-card__cover-image--active {
  z-index: 3;
  opacity: 0;
  transition: opacity 0.24s ease;
}

.image-card__cover-image--active.is-loaded {
  opacity: 1;
}

.image-card__cover-image--active.is-fading-in {
  animation: image-cover-fade-in 0.42s cubic-bezier(0.2, 0, 0.2, 1) both;
}

.image-card:hover :deep(.media-sound-toggle) {
  opacity: 1;
  pointer-events: auto;
}

.image-card:hover :deep(.cover-meta__bgm),
.image-card:focus-within :deep(.cover-meta__bgm) {
  animation-play-state: running;
}

.image-card__controls {
  position: absolute;
  top: 50%;
  right: 8px;
  left: 8px;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-50%) scale(0.98);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.image-card:hover .image-card__controls,
.image-card:focus-within .image-card__controls,
.image-card__controls.is-pending {
  opacity: 1;
  transform: translateY(-50%) scale(1);
}

.image-card__control {
  display: grid;
  width: 24px;
  height: 24px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  outline: none;
  pointer-events: auto;
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.1));
  transform: scale(1);
  transition:
    color 0.16s ease,
    filter 0.16s ease,
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.image-card__control:hover,
.image-card__control:focus-visible {
  color: rgba(255, 255, 255, 0.88);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.18));
  transform: scale(var(--app-interaction-object-scale-hover));
}

.image-card__control:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.image-card__control:disabled {
  cursor: default;
}

.image-card__control:disabled:not(.is-loading) {
  color: rgba(255, 255, 255, 0.42);
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.06));
  transform: scale(1);
}

.image-card__control svg {
  display: block;
}

.image-card__loading-ring {
  display: block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.38);
  border-top-color: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  animation: image-control-spin 0.72s linear infinite;
}

@keyframes image-cover-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes image-cover-fade-out {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes image-control-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .image-card__cover-image--previous,
  .image-card__cover-image--active.is-fading-in,
  .image-card__loading-ring {
    animation: none;
  }

  .image-card__cover-image--preview,
  .image-card__cover-image--active {
    transition: none;
  }

  .image-card__controls,
  .image-card__control {
    transition: opacity 0.18s ease;
  }

  .image-card:hover .image-card__controls,
  .image-card:focus-within .image-card__controls,
  .image-card__control:hover,
  .image-card__control:focus-visible {
    transform: translateY(-50%);
  }

  .image-card__control:hover,
  .image-card__control:focus-visible {
    transform: none;
  }
}
</style>
